<template>
  <div class="login-card-content verification-code">
    <LoginCardHeading
      title="Código de autorização"
      :description="getDescription"
    />

    <div class="verification-code__otp-input">
      <OTPInput
        :digits-count="6"
        :has-error="serverValidationHasFailed"
        :error-message="getErrorMessageByField('2fa_code')"
        @full-empty="resetServerValidations"
        @full-filled="submitVerificationCode"
      />

      <div class="verification-code__resend-code">
        <h2>Não recebeu o código?</h2>

        <h3 v-if="!displayCountdown">
          <a href="javascript:;" title="Clique aqui para reenviar o código de autorização" @click.prevent="resendVerificationCode">Clique aqui para reenviar</a>.
        </h3>

        <small
          v-else
          class="verification-code__countdown"
        >
          Reenviar o código em

          <VueCountdown
            v-slot="{ minutes, seconds }"
            :time="60000"
            :transform="formatValuesLowerThanZero"
            @end="finishCountdown"
          >
            {{ minutes }}:{{ seconds }}
          </VueCountdown>
        </small>
      </div>
    </div>

    <LoginCardDisclaimer
      link-text="Enviar código por outro meio"
      @handle-disclaimer="$emit('step-changed', getSteps.SECOND)"
    />
  </div>
</template>

<script>
// DEPENDENCIES
import VueCountdown from "@chenfengyuan/vue-countdown";
import {formatValuesLowerThanZero} from "commons/js/config/vue-countdown";

// UTILS
import recaptcha from "commons/js/ReCAPTCHAv3";

// COMPONENTS
import OTPInput from "commons/js/components/atoms/OTPInput";
import LoginCardHeading from "@home/js/components/login/LoginCardHeading";
import LoginCardDisclaimer from "@home/js/components/login/LoginCardDisclaimer";

// MIXINS
import formValidator from "commons/js/mixins/formValidator";
import serverValidator from "commons/js/mixins/serverValidator";

// CONFIG
import { TWO_FACTOR_SERVICES_ENUM } from "@home/js/components/login/2FA/config";

// CONSTANTS
const SUBMIT_DELAY = 1000;

export default {
  name: "VerificationCode",

  inject: [
    "user",
    "channel",
    "getSteps",
    "remember"
  ],

  mixins: [
    formValidator,
    serverValidator
  ],

  components: {
    OTPInput,
    VueCountdown,
    LoginCardHeading,
    LoginCardDisclaimer
  },

  data() {
    return {
      displayCountdown: false,
    }
  },

  computed: {
    /**
     * Checks if the current selected 2FA channel is the user e-mail.
     *
     * @returns {boolean}
     */
    isUsingEmail() {
      return this.channel() === TWO_FACTOR_SERVICES_ENUM.EMAIL
    },

    /**
     * Gets the properly description to be used within the card.
     * Its based on the selected service to receive the 2FA verification code.
     *
     * @returns {void}
     */
    getDescription() {
      const description = "Identificamos que você está acessando sua conta de um novo dispositivo ou iniciando uma nova sessão.\n\n";

      return this.isUsingEmail ? `${description} Para continuar, digite o código de 6 dígitos que enviamos para o e-mail ${this.user().email}.` : `${description}`;
    }
  },

  methods: {
    formatValuesLowerThanZero,

    startCountdown() {
      this.displayCountdown = true;
    },

    finishCountdown() {
      this.displayCountdown = false;
    },

    /**
     * Handles the logic of resending a 2FA verification code.
     *
     * @returns {void}
     */
    async resendVerificationCode() {
      this.startCountdown();

      recaptcha('login_verification_code', async (token) => {
        const payload = {
          username: this.user().name,
          password: this.user().password,
          token: token,
          channels: [this.channel()]
        };

        await axios.post('/login', payload);
      })
      .catch((error) => {
        this.handleError(error)
      });
    },

    /**
     * Handles the login using a 2FA verification code.
     *
     * @returns {void}
     */
    async submitVerificationCode({code}) {
      this.$emit('start-loading');
      this.resetServerValidations();

      recaptcha('login_verification_code', async (token) => {
        try {
          const payload = {
            username: this.user().name,
            password: this.user().password,
            token: token,
            remember: this.remember(),
            '2fa_code': code
          };

          const {data} = await axios.post('/login', payload);

          this.$emit('handle-redirect', data);
        } catch (error) {
          this.handleError(error);
        } finally {
          this.$emit('finish-loading');
        }
      });
    }
  },
}
</script>

<style lang="stylus" scoped>
.verification-code

  &__otp-input
    margin-top 24px
    margin-bottom 32px

    @media screen and (min-width: 940px)
      margin-top 0
      margin-bottom 48px

  &__resend-code
    display flex
    flex-direction column
    align-items center
    margin-top 32px
    font-size 14px
    line-height 20px

  &__countdown
    font-size 14px
    line-height 20px
    color #697882
</style>
